import type { FileManagerMount } from '@/components/document/sub-components/file-manager';
import { entraAuthService } from '@/services/auth/entra-auth-service';
import { GraphRequestError } from '@/services/graph/graph-request-error';
import { sharePointDriveService } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { useQuery } from '@tanstack/vue-query';
import type { models } from 'cesg-shared';
import {
  audienceOf,
  PermissionType,
  SITE_LIBRARIES,
  UserRole,
} from 'cesg-shared/dist/models';
import { computed } from 'vue';
import { useProjectSitesQuery } from './data/sharepoint-sites/useProjectSitesQuery';
import {
  canBrowse,
  computeFileManagerPermissions,
} from './file-manager-capabilities';

/** One entry of the provisioning spec's library list — the source of a mount's title and audience. */
export type SiteLibrarySpec = (typeof SITE_LIBRARIES)[number];

export interface ProjectSyncTarget {
  projectId: string;
  title: string;
  libraries: { title: string; driveId: string }[];
}

const mountKeyFor = (siteId: string, specKey: string) => `${siteId}:${specKey}`;

/**
 * Is there a mount table to browse, are we still finding out, or did finding out fail.
 *
 * Owned by the host rather than by the file manager (D4): the file manager only ever receives a
 * non-empty, authoritative table, so it has no loading/unprovisioned state of its own to reason
 * about. A host renders the matching notice *instead of* the file manager.
 *
 * `'available'` is exactly `mounts.length > 0` — including "every probe came back denied", which is
 * indistinguishable from "nothing provisioned" from the user's side and gets the same notice.
 *
 * `'no-access'` is narrower than that on purpose: the site IS provisioned, and the reason there is
 * nothing to browse is this user's own `files` level. It deliberately ranks *after* `'unprovisioned'`
 * — with no site at all there is nothing to have access to, so "not created yet" is the truer answer.
 * (The old pane-level gate ordered these the other way round; the case only arises for someone deep
 * linking past a tab that is already permission-gated.)
 */
export type ProjectMountsStatus =
  | 'loading'
  | 'unprovisioned'
  | 'unavailable'
  | 'no-access'
  | 'available';

export interface UseProjectMountsOptions {
  /**
   * Where a site's library is grafted into the path namespace. The project page uses `/${spec.title}`
   * (one segment, one project at the root); the admin page prefixes the project as well
   * (`/1234 - Foo/CESG`). Must sanitize '/' out of every segment it builds from data (D1) — otherwise
   * a stray slash in a project title silently invents a namespace level.
   */
  prefixOf: (site: models.SharePointSite, spec: SiteLibrarySpec) => string;
  /** Narrows the server's "every site I can see" down to the ones this host is about. */
  filter?: (site: models.SharePointSite) => boolean;
  /** See probeAccessibleMounts. Off by default: it costs one Graph call per mount (D5). */
  probe?: boolean;
}

/**
 * Replaces '/' in a value that is about to become one path segment. A project title comes from
 * Fortnox, so it is data, not code: an unsanitized slash would split into two namespace levels and
 * make the mount unreachable through the UI that built the path (D1).
 */
export const sanitizePathSegment = (value: string): string =>
  value.replace(/\//g, '-');

/**
 * Grafts a lone library at the virtual root, so a user who can reach exactly one lands inside it
 * instead of double-clicking through a listing with one row in it.
 *
 * Applied by the PAGE rather than folded into useProjectMounts below: "a lone library should take the
 * root" is a project-page product decision, not part of building the table. The admin page wants the
 * opposite — it labels every level so several projects can coexist — so a single-project, single-
 * library tenant still reads there as `1234 - Foo / Delad`.
 *
 * Must be applied to the FINAL, probed table, and that is not a choice: capabilities are computed per
 * PROJECT (see capabilitiesFor), so both of a project's libraries carry identical permissions and
 * nothing before the probe tells them apart. A GUEST has two readable candidates right up until
 * Graph answers 403 for the CESG library — which is the entire case this exists for.
 *
 * Keyed on table LENGTH, not on the number of distinct driveIds. The two agree today, but two mounts
 * sharing a drive would both collapse onto '/' and then compare as the same mount — precisely the
 * hazard `key` uniqueness (D8) exists to prevent. Length is also what upholds the file manager's own
 * "a root mount must be the table's only mount" invariant: the root lists the drive, so a second
 * mount would have no row anywhere.
 */
export const collapseSoleMount = (
  mounts: FileManagerMount[],
): FileManagerMount[] =>
  mounts.length === 1 ? [{ ...mounts[0], prefix: '/' }] : mounts;

/**
 * Decides which of the ledger's mounts this user can actually browse, by asking SharePoint.
 *
 * This is the whole reason a GUEST sees only `Delad`: the answer comes from Graph's response to a
 * real request, not from a client-side role check, so the enforcement point stays SharePoint alone
 * (the server deliberately hands us mounts we may not be able to read). A 403/404 hides the mount;
 * any other status keeps it listed, because "the network hiccuped" must not look the same as "you
 * have no access here".
 *
 * The probe doubles as id resolution: getDriveRoot returns the drive's root item id, so an
 * accessible mount carries a real `rootId` and a drop onto its root needs no extra round-trip.
 */
const probeAccessibleMounts = async (
  candidates: FileManagerMount[],
): Promise<FileManagerMount[]> => {
  const results = await Promise.allSettled(
    candidates.map((candidate) =>
      sharePointDriveService.getDriveRoot(candidate.driveId),
    ),
  );

  return candidates.flatMap((candidate, index) => {
    const result = results[index];
    if (result.status === 'fulfilled') {
      return [{ ...candidate, rootId: result.value.rootId }];
    }
    const isDenied =
      result.reason instanceof GraphRequestError &&
      (result.reason.status === 403 || result.reason.status === 404);
    return isDenied ? [] : [candidate];
  });
};

/**
 * Builds the mount table the file manager takes as its single inbound prop, from the provisioning
 * ledger plus the code-owned site spec.
 *
 * Everything project-shaped lives here, on purpose (D4): the sites query, the ledger→spec join, the
 * `{number} - {name}` display formatting a caller does inside `prefixOf`, and the accessibility
 * probe. The file manager below receives paths, driveIds and capability tokens and has no idea a
 * project or a SharePoint site exists — which is what lets one host list a single project's libraries
 * and another list every project's, with no new concept inside the component.
 */
export const useProjectMounts = ({
  prefixOf,
  filter,
  probe = false,
}: UseProjectMountsOptions) => {
  const { data: sites, isError: rawIsSitesError } = useProjectSitesQuery();
  // useQuery's isError comes back as a discriminated union of Ref<true>/Ref<false> (narrowed per
  // query status) rather than a plain ComputedRef<boolean> — flatten it once here so the derivation
  // below reads as ordinary boolean logic.
  const isSitesError = computed(() => rawIsSitesError.value);

  const visibleSites = computed(() => {
    const all = sites.value ?? [];
    return filter ? all.filter(filter) : all;
  });

  // Real input, per the design the permission system was always headed for: the signed-in user's role
  // plus the project's own `Permission.files` level (D12). `files` is its own DB resource rather than a
  // reuse of `overview` because the value now becomes a durable SharePoint ACL — bumping someone's
  // `overview` so they could read a dashboard number would otherwise grant them delete rights in the
  // project's document library.
  const role = computed<UserRole>(
    () => entraAuthService.me.value?.role ?? UserRole.GUEST,
  );
  const capabilitiesFor = (projectId: string) =>
    computeFileManagerPermissions(
      entraAuthService.me.value?.permissions.find(
        (permission) => permission.projectId === projectId,
      )?.files ?? PermissionType.DISABLED,
      role.value,
    );

  // Composed by iterating the spec rather than the ledger rows: that fixes display order, supplies
  // the title the virtual paths are built from, and silently drops any stored key the spec no longer
  // declares (a library removed from SITE_LIBRARIES stops being browsable without a migration).
  const candidates = computed<FileManagerMount[]>(() =>
    visibleSites.value.flatMap((site) => {
      const permissions = capabilitiesFor(site.projectId);
      return SITE_LIBRARIES.flatMap((spec) => {
        const row = site.libraries.find(
          (candidate) => candidate.key === spec.key,
        );
        return row
          ? [
              {
                // Site-qualified, because `key` must be unique across the WHOLE table (D8): with a
                // bare spec key, two projects' `SHARED` mounts would compare equal and a drop from
                // one into the other would be attempted as a same-drive move.
                key: mountKeyFor(site.siteId, spec.key),
                driveId: row.driveId,
                prefix: prefixOf(site, spec),
                audience: audienceOf(spec.grants),
                permissions,
              },
            ]
          : [];
      });
    }),
  );

  // The app-level `files` gate, applied by dropping the mount rather than by hiding UI inside the
  // pane (D6). Load-bearing, not cosmetic: SharePoint's own ACL is keyed on role family, so a CESG
  // user with `files: DISABLED` would still be *able* to read the drive — not listing the mount is
  // what makes the app's own gate hold.
  const readable = computed(() =>
    candidates.value.filter((mount) => canBrowse(mount.permissions)),
  );

  const { data: probed } = useQuery({
    // Keyed by the drives being probed rather than by a site id, so it is correct for a table
    // spanning any number of sites. staleTime Infinity keeps it to once per session per drive set
    // (it costs one Graph call per mount and, being the first Graph call made, is what triggers the
    // MSAL popup).
    queryKey: computed(() => [
      'sharepoint-libraries',
      ...readable.value.map((candidate) => candidate.driveId),
    ]),
    queryFn: () => probeAccessibleMounts(readable.value),
    staleTime: Infinity,
    // getDriveRoot already retries transient Graph failures internally (withRetry), and this query
    // must not turn a per-mount outcome into a whole-probe retry loop — same single-layer-retry
    // reasoning as useDirectoryChildrenQuery.
    retry: false,
    enabled: computed(() => probe && readable.value.length > 0),
  });

  const mounts = computed<FileManagerMount[]>(() =>
    probe ? (probed.value ?? []) : readable.value,
  );

  const syncTargets = computed<ProjectSyncTarget[]>(() => {
    const reachable = new Set(mounts.value.map((mount) => mount.key));

    return visibleSites.value.flatMap((site) => {
      const libraries = SITE_LIBRARIES.flatMap((spec) => {
        const row = site.libraries.find(
          (candidate) => candidate.key === spec.key,
        );
        if (!row || !reachable.has(mountKeyFor(site.siteId, spec.key))) {
          return [];
        }
        return [{ title: spec.title, driveId: row.driveId }];
      });

      return libraries.length
        ? [{ projectId: site.projectId, title: site.title, libraries }]
        : [];
    });
  });

  const status = computed<ProjectMountsStatus>(() => {
    // Deliberately reads `sites === undefined` rather than the error flag alone: vue-query retains
    // `data` when a *refetch* fails, and the ledger is refetched on window focus — reading the flag
    // would tear the whole pane down and lose the user's folder over a network blip.
    if (sites.value === undefined)
      return isSitesError.value ? 'unavailable' : 'loading';
    if (candidates.value.length === 0) return 'unprovisioned';
    if (readable.value.length === 0) return 'no-access';
    if (probe && probed.value === undefined) return 'loading';
    return mounts.value.length > 0 ? 'available' : 'unprovisioned';
  });

  return { mounts, syncTargets, status };
};
